
import {computed, defineComponent, PropType, ref} from "vue";
import {ExportInterfaceDto, PaginationDto} from "@dex/squeeze-client-ts";
import Dropdown from "primevue/dropdown";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Calendar from 'primevue/calendar';
import InputText from "primevue/inputtext";
import {ExportStatus} from "@dex/squeeze-client-ts/dist/api";
import {useI18n} from "vue-i18n";
import {formatDate as formatDateUtil} from "@/util/DateHelper";
import Tooltip from "primevue/tooltip";
import { DocumentFilterObject } from "@/apps/squeeze/interfaces/DocumentSearch";
import {FilterMatchMode} from "primevue/api";

export default defineComponent({
	name: 'DocumentClassExportStates',
	components: {
		Dropdown,
		DataTable,
		Column,
		Calendar,
		InputText,
	},
	props: {
		/** Show loading in the table? */
		loading: {
			type: Boolean,
			default: false,
		},
		/** All export states */
		exportStates: {
			type: Array as PropType<ExportStatus[]>,
			default: () => [],
		},
		paginationInfo: {
			type: Object as PropType<PaginationDto>,
		},
		allExportInterfaces: {
			type: Array as PropType<ExportInterfaceDto[]>,
			default: () => [],
			required: true,
		},
	},
	directives: {
		'tooltip': Tooltip,
	},
	emits: [
		"onPage",
	],
	setup(props, {emit}) {
		const {t, locale} = useI18n();

		/** Filters of list (Currently not activated) */
		const filters = ref<DocumentFilterObject>({});

		/** Should the clear filter button be shown in the table? */
		const showBtnClearFilter = ref<boolean>(false);

		/** ExportStates for filter selection */
		const statuses =  ref<string[]>([
			'ERROR', 'FINISHED', 'EXPORTED', 'ASYNC_WAITING', 'DELETED_BEFORE_EXPORTED', 'MOVED_BEFORE_EXPORTED',
		]);

		/** Triggered when the next page is selected */
		const onPage = (event: PaginationDto) => {
			emit("onPage", event);
		}

		/**
		 * Gets the Name of an export interface
		 * @param interfaceId Id of the export interface to get the name
		 * @return Name of the export Interface
		 */
		const getNameFromExportInterface = (interfaceId: number) => {
			if (interfaceId == null) {
				return t('Squeeze.General.None');
			}
			const exportInterface = props.allExportInterfaces.find(i => i.id === interfaceId);

			if (!exportInterface) {
				return String(interfaceId)
			}
			return exportInterface.interfaceName
		}

		/**
		 * Triggered when a filter has been entered
		 * @param event
		 */
		const onFilter = (event: any) => {
			showBtnClearFilter.value = event.filteredValue.length < props.exportStates.length;
		}

		/** Init filters */
		const initFilters = () => {
			filters.value = {
				"id": {value: null, matchMode: FilterMatchMode.EQUALS},
				"documentId": {value: null, matchMode: FilterMatchMode.EQUALS},
				"interfaceName": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"status": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"result": {value: null, matchMode: FilterMatchMode.CONTAINS},
				"timeStampDate": {value: null, matchMode: FilterMatchMode.DATE_IS},
			};
		}

		/** Clear filters */
		const clearFilters = () => {
			initFilters();
		}

		initFilters();

		/** Export states enriched with a resolved interfaceName for name-based filtering */
		const exportStatesWithNames = computed(() =>
			props.exportStates.map(state => ({
				...state,
				interfaceName: getNameFromExportInterface(state.interfaceId!),
				formattedTimeStamp: formatDateUtil(state.timeStamp!, locale.value),
				timeStampDate: state.timeStamp ? new Date(state.timeStamp) : null,
			}))
		);

		return {
			t,
			filters,
			exportStatesWithNames,
			showBtnClearFilter,
			statuses,
			onPage,
			getNameFromExportInterface,
			onFilter,
			clearFilters,
		}
	},
})
